import { Link } from "react-router-dom";
import Breadcrumb from "@/components/video/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const platforms = [
  {
    key: "android",
    name: "Android 安卓版",
    version: "v3.8.2",
    size: "42.6 MB",
    requirement: "Android 6.0 及以上",
    icon: "ri-android-line",
    url: "https://fljmen.cc/app/android/cucumber-video-v3.8.2.apk",
    file: "cucumber-video-v3.8.2.apk",
  },
  {
    key: "ios",
    name: "iOS 苹果版",
    version: "v3.8.0",
    size: "58.1 MB",
    requirement: "iOS 12.0 及以上",
    icon: "ri-apple-line",
    url: "https://fljmen.cc/app/ios/cucumber-video",
    file: "",
  },
];

const features = [
  {
    icon: "ri-hd-line",
    title: "高清画质",
    desc: "支持 1080P 及以上的高清视频播放体验，画面清晰细腻，带来更沉浸的观看感受。",
  },
  {
    icon: "ri-download-cloud-2-line",
    title: "离线下载",
    desc: "喜欢的视频可以提前下载到本地，没有网络也能随时回看，通勤旅途都不无聊。",
  },
  {
    icon: "ri-device-line",
    title: "多端同步",
    desc: "手机、平板与 PC 端观看记录互通，换设备接着看，进度不丢失。",
  },
  {
    icon: "ri-flashlight-line",
    title: "极速播放",
    desc: "播放器启动迅速、拖动流畅，支持清晰度切换与倍速播放等实用功能。",
  },
  {
    icon: "ri-stack-line",
    title: "内容丰富",
    desc: "覆盖电影、电视剧与短视频多种类型，持续更新热门内容，总有一款合你口味。",
  },
  {
    icon: "ri-shield-check-line",
    title: "安全纯净",
    desc: "安装包经过安全检测，界面简洁清爽，观看过程更安心。",
  },
];

const steps = [
  { step: "01", title: "选择平台", desc: "根据你的手机系统，选择安卓或苹果版本。" },
  { step: "02", title: "点击下载", desc: "点击对应按钮开始下载安装包（当前为示范地址）。" },
  { step: "03", title: "完成安装", desc: "下载完成后按照提示完成安装，即可开始观看。" },
];

export default function Download() {
  useDocumentTitle(
    "App下载 - 黄瓜视频App官方网站",
    "下载黄瓜视频App安卓版与iOS版安装包，体验高清视频播放与离线下载，涵盖电影、电视剧与短视频内容。",
    "黄瓜视频App下载,视频App,安卓下载,iOS下载,高清视频App",
  );

  return (
    <div className="w-full">
      <section className="relative w-full h-[440px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20floating%20above%20a%20dark%20surface%20with%20a%20glowing%20video%20play%20interface%2C%20warm%20orange%20light%20trails%20and%20cool%20teal%20rim%20light%2C%20minimal%20tech%20atmosphere%2C%20high%20contrast%2C%20cinematic&width=1600&height=900&seq=download-hero&orientation=landscape"
          alt="黄瓜视频App 下载引导背景"
          title="黄瓜视频App下载 - 高清视频播放与下载"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/75 to-background-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-black/20" />
        <div className="relative z-10 w-full h-full px-4 md:px-8 pt-24 md:pt-32 pb-10 flex flex-col justify-center">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "App下载" }]} />
          <h1 className="mt-4 text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight max-w-2xl">
            下载黄瓜视频App
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-300 max-w-xl leading-relaxed">
            选择你的设备平台，一键下载安装，随时随地享受高清视频播放与离线下载体验。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 -mt-6 md:-mt-10 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {platforms.map((p) => (
            <div
              key={p.key}
              className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-500/70 transition-colors animate-fade-up"
            >
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-lg bg-primary-500/15 text-primary-400 flex items-center justify-center">
                  <i className={`${p.icon} text-3xl`} />
                </span>
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">{p.name}</h2>
                  <p className="mt-1 text-sm text-foreground-500">
                    {p.version} · {p.size}
                  </p>
                </div>
              </div>
              <p className="mt-5 text-sm text-foreground-400">系统要求：{p.requirement}</p>
              <a
                href={p.url}
                download={p.file || undefined}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-2 w-full h-12 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line w-5 h-5 flex items-center justify-center" />
                立即下载
              </a>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-foreground-500 flex items-center gap-2">
          <i className="ri-information-line w-4 h-4 flex items-center justify-center text-accent-400" />
          当前下载链接为示范地址，正式上线时可替换为真实安装包地址。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-sm font-label text-accent-400 tracking-wide">Features</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-heading font-bold text-foreground-950">App 功能亮点</h2>
          <p className="mt-3 text-sm text-foreground-500">从画质到下载，从体验到内容，为你逐一抹平观看路上的小麻烦。</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((f) => (
            <div key={f.title} className="p-6 rounded-lg bg-background-50 border border-background-200/60">
              <span className="w-12 h-12 rounded-lg bg-accent-500/15 text-accent-400 flex items-center justify-center">
                <i className={`${f.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm text-foreground-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-sm font-label text-primary-400 tracking-wide">How to</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-heading font-bold text-foreground-950">三步完成安装</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {steps.map((s) => (
            <div key={s.step} className="p-6 rounded-lg bg-background-100 border border-background-200/60">
              <span className="text-3xl font-heading font-bold text-primary-500">{s.step}</span>
              <h3 className="mt-3 text-base font-heading font-semibold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300/70 hover:border-primary-500 text-foreground-800 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line w-5 h-5 flex items-center justify-center" />
            返回首页浏览视频
          </Link>
        </div>
      </section>
    </div>
  );
}